import type { AxiosRequestConfig } from 'axios'

import { callAPI, laborerAuthAPIWithCredentials } from 'api/axios'
import { LIST_ITEMS_PER_PAGE } from 'common/constants'
import { GeneralFailResponse } from 'common/types/commonTypes'

export type WorkRefusalRequestStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REJECTED' | 'CANCELLED'

export enum WorkRefusalContractType {
  IND2IND = 'IND2IND',
  ETAWTHEEQ = 'ETAWTHEEQ',
  CONTRACT_AUTHENTICATION = 'CONTRACT_AUTHENTICATION',
  EST2IND = 'EST2IND',
}

export type WorkRefusalReason = {
  id: string
  label: string
  labelEn: string
}

export type WorkRefusalRequestFromBE = {
  id: number
  sponsor: {
    name: string
    id_number: number
  }
  contract: {
    id: number
    start_date: string
    end_date: string
    status: string
    relatable_id: number
    relatable_source: WorkRefusalContractType
  }
  status: WorkRefusalRequestStatus
  reason: WorkRefusalReason['id']
  created_at?: string
  cancelled_at: string | null
  activated_at: string | null
}

export type WorkRefusalRequestDetailsFromBE = {
  id: number
  sponsor: {
    name: string
    id_number: number
  }
  contract: {
    id: number
    start_date: string
    end_date: string
    status: string
    relatable_id: number
    relatable_source: WorkRefusalContractType
  }
  labor: {
    name: string
    name_en: string
    iqama_number: string
    passport_number: string
    nationality: string
    nationality_en: string
  }
  employer: {
    name: string
    nationality: string
    id_number: string
    mobile_number: string
  }
  status: WorkRefusalRequestStatus
  reason: WorkRefusalReason['id']
  cancelled_at: string | null
  activated_at: string | null
  created_at: string
}

export type GetWorkRefusalRequestsParams = {
  page?: number
  perPage?: number
}

export type GetWorkRefusalRequestsResponse = {
  data: Array<WorkRefusalRequestFromBE>
}

const getWorkRefusalRequests = (
  { page = 1, perPage = LIST_ITEMS_PER_PAGE }: GetWorkRefusalRequestsParams = {},
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.get<GetWorkRefusalRequestsResponse>('api/laborer/work-refusal-requests', {
    params: {
      page,
      per_page: perPage,
    },
    ...config,
  })

export type GetWorkRefusalRequestDetailsParams = {
  requestId: number
}

export type GetWorkRefusalRequestDetailsResponse = {
  data: WorkRefusalRequestDetailsFromBE
  token: string
}

const getWorkRefusalRequestDetails = (
  { requestId }: GetWorkRefusalRequestDetailsParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.get<GetWorkRefusalRequestDetailsResponse>(
    `api/laborer/work-refusal-requests/${requestId}`,
    config,
  )

export type UpdateWorkRefusalRequestParams = {
  requestId: number
}

export type ApproveWorkRefusalRequestResponse = void

const approveWorkRefusalRequest = ({ requestId }: UpdateWorkRefusalRequestParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<ApproveWorkRefusalRequestResponse>(
    `api/laborer/work-refusal-requests/${requestId}/approve`,
    undefined,
    config,
  )

export type RejectWorkRefusalRequestResponse = void

const rejectWorkRefusalRequest = ({ requestId }: UpdateWorkRefusalRequestParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<RejectWorkRefusalRequestResponse>(
    `api/laborer/work-refusal-requests/${requestId}/reject`,
    undefined,
    config,
  )

export default {
  getWorkRefusalRequests: callAPI<typeof getWorkRefusalRequests, GetWorkRefusalRequestsResponse, GeneralFailResponse>(
    getWorkRefusalRequests,
  ),
  getWorkRefusalRequestDetails: callAPI<
    typeof getWorkRefusalRequestDetails,
    GetWorkRefusalRequestDetailsResponse,
    GeneralFailResponse
  >(getWorkRefusalRequestDetails),
  approveWorkRefusalRequest: callAPI<
    typeof approveWorkRefusalRequest,
    ApproveWorkRefusalRequestResponse,
    GeneralFailResponse
  >(approveWorkRefusalRequest),
  rejectWorkRefusalRequest: callAPI<
    typeof rejectWorkRefusalRequest,
    RejectWorkRefusalRequestResponse,
    GeneralFailResponse
  >(rejectWorkRefusalRequest),
}
